import type { Dealer } from '@features/dealers/types/dealers.types';
import { minimumConfiguredServices } from '@features/service-plans/utils/minimumConfiguredServices';
import type { QuoteData, QuoteSummary } from '../types/quote.types';
import type { PricedBasket } from '../types/basketPricing.types';
import { isValidPaymentSelection } from '../types/schedule.types';
import { basketPricingInputFrom, mergePricedLines } from '../utils/pricedLines';
import { normaliseLegacyFeeVat } from '../utils/legacyFeeVat';
import { decimalToNumber } from '../utils/decimalMoney';
import { resolveFirstCollectionDate } from './firstCollectionDate';
import type { QuoteSnapshot, SavedQuote } from './quoteSaveLane';

export interface SnapshotContext {
    defer: boolean;
    homeDealer: Dealer | null;
}

const firstCollectionDateOf = (paymentSelection: QuoteData['paymentSelection']) => {
    const firstCollection = isValidPaymentSelection(paymentSelection)
        ? resolveFirstCollectionDate(paymentSelection)
        : ({ kind: 'unknown' } as const);
    return firstCollection.kind === 'date' ? firstCollection.date : null;
};

/** Null when the quote cannot be saved yet. */
export const buildQuoteSnapshot = (
    quote: QuoteData,
    { defer, homeDealer }: SnapshotContext,
): QuoteSnapshot | null => {
    const effectiveDealer = quote.selectedDealer ?? (defer ? homeDealer : null);

    // Must stay in lockstep with the 'configuration' arm of useStepBlockReason.
    if (
        !effectiveDealer ||
        !quote.selectedVehicle ||
        !quote.selectedProduct ||
        !Array.isArray(quote.configuredServices) ||
        quote.configuredServices.length < minimumConfiguredServices(quote.selectedProduct)
    ) {
        return null;
    }

    const summary: QuoteSummary = {
        dealer: effectiveDealer,
        vehicle: quote.selectedVehicle,
        product: normaliseLegacyFeeVat(quote.selectedProduct),
        services: quote.configuredServices,
        addOns: Array.isArray(quote.configuredAddOns) ? quote.configuredAddOns : [],
        payment: quote.paymentOptions,
        paymentSelection: quote.paymentSelection,
        scheduleOverrides: quote.scheduleOverrides,
        customer: quote.selectedCustomer,
        estimated_start_date: firstCollectionDateOf(quote.paymentSelection),
        anchorDate: quote.anchorDate ?? null,
        resolvedPlanId: quote.resolvedPlanId ?? null,
        servicePlanSessionId: quote.servicePlanSessionId ?? null,
        smriqConfirmedVehicle: quote.smriqConfirmedVehicle ?? null,
        smriqPlanSettings: quote.smriqPlanSettings ?? null,
        dealerProvisional: !quote.selectedDealer && defer,
    };

    return {
        draftId: quote.draftId,
        quoteId: quote.quoteId,
        version: quote.version,
        summary,
        pricingInput: basketPricingInputFrom(quote),
    };
};

/** Throws when a configured line the response did not cover, or flagged unpriced, would be sent. */
export const applyPricing = (summary: QuoteSummary, pricing: PricedBasket): QuoteSummary => {
    const merged = mergePricedLines(summary.services, summary.addOns, pricing);
    if (!merged.complete || merged.unpricedNames.length > 0) {
        throw new Error('Cannot save quote: pricing is incomplete for the configured basket.');
    }
    return {
        ...summary,
        services: merged.services,
        addOns: merged.addOns,
        pricingVersion: pricing.pricingVersion,
        total_cost: decimalToNumber(pricing.totals.net),
    };
};

export const toSavedQuote = (response: QuoteSummary): SavedQuote => {
    if (response.version === undefined) {
        throw new Error('Cannot record a saved quote without its version.');
    }
    return {
        id: response.id ?? '',
        quoteRef: response.quote_ref ?? '',
        status: response.status ?? 'live',
        version: response.version,
        updatedAt: response.updatedAt ?? null,
    };
};
